/**
 * Voice Service
 * Handles voice cloning and TTS operations
 */

import { AudioPodClient } from '../client/AudioPodClient';
import {
  VoiceCloneRequest,
  VoiceCloneResult,
  VoiceProfile,
  Job,
  UploadProgress,
  VoiceConversionRequest,
  VoiceConversionResult,
  MultiVoiceTTSRequest,
  MultiVoiceTTSResult,
  VoiceCollectionRequest,
  VoiceCollectionResult,
  VoiceCollection,
  PublicPreviewRequest,
  PublicPreviewResult
} from '../types';

export class VoiceService {
  constructor(private client: AudioPodClient) {}

  /**
   * Clone a voice from an audio file
   * This method first creates a voice profile, then uses it for cloning
   */
  async cloneVoice(request: VoiceCloneRequest): Promise<VoiceCloneResult> {
    const {
      voiceFile,
      text,
      language,
      speed = 1.0,
      waitForCompletion = false,
      timeout = 300000
    } = request;

    // Validate inputs
    if (!text || text.trim().length === 0) {
      throw new Error('Text input cannot be empty');
    }

    // Step 1: Create a temporary voice profile from the uploaded file
    const voiceProfileName = `temp_voice_${Date.now()}`;
    const voiceProfile = await this.createVoiceProfile(
      voiceProfileName,
      voiceFile,
      'Temporary voice profile for cloning',
      false, // not public
      true,  // wait for completion
      timeout
    );

    // Step 2: Use the voice profile for cloning
    const data = {
      voice_id: voiceProfile.id,
      input_text: text.trim(),
      target_language: language?.toLowerCase(),
      generation_params: speed !== 1.0 ? { speed } : undefined
    };

    const job = await this.client.post<Job>('/voice/voice-clone', data);

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        outputUrl: result.output_url || result.outputUrl,
        duration: result.duration
      };
    }

    return { job };
  }

  /**
   * Create a reusable voice profile
   */
  async createVoiceProfile(
    name: string,
    voiceFile: File | Blob | string,
    description?: string,
    isPublic: boolean = false,
    waitForCompletion: boolean = false,
    timeout: number = 600000
  ): Promise<VoiceProfile> {
    if (!name || name.trim().length === 0) {
      throw new Error('Voice profile name cannot be empty');
    }

    if (name.length > 100) {
      throw new Error('Voice profile name too long (max 100 characters)');
    }

    let fileData: File | Blob;

    if (typeof voiceFile === 'string') {
      const fs = await import('fs');
      const path = await import('path');

      if (!fs.existsSync(voiceFile)) {
        throw new Error(`Voice file not found: ${voiceFile}`);
      }

      const fileBuffer = fs.readFileSync(voiceFile);
      const fileName = path.basename(voiceFile);
      fileData = new Blob([fileBuffer], { type: 'audio/*' });
      (fileData as any).name = fileName;
    } else {
      fileData = voiceFile;
    }

    const formData: Record<string, any> = {
      name: name.trim(),
      is_public: isPublic
    };

    if (description) {
      formData.description = description.trim();
    }

    const response = await this.client.uploadFile<VoiceProfile>(
      '/voice/voice-profiles',
      fileData,
      formData
    );

    if (waitForCompletion) {
      // Poll for voice profile completion
      const startTime = Date.now();
      while (Date.now() - startTime < timeout) {
        const voiceProfile = await this.getVoiceProfile(response.id);

        if (voiceProfile.status === 'completed') {
          return voiceProfile;
        } else if (voiceProfile.status === 'failed') {
          throw new Error(`Voice profile creation failed: ${voiceProfile.status}`);
        }

        await new Promise(resolve => setTimeout(resolve, 5000));
      }

      throw new Error('Voice profile creation timed out');
    }

    return response;
  }

  /**
   * Generate speech using an existing voice profile
   */
  async generateSpeech(
    voiceId: number | string,
    text: string,
    options: {
      language?: string;
      speed?: number;
      audioFormat?: 'mp3' | 'wav';
      waitForCompletion?: boolean;
      timeout?: number;
    } = {}
  ): Promise<VoiceCloneResult> {
    const {
      language,
      speed = 1.0,
      audioFormat = 'mp3',
      waitForCompletion = false,
      timeout = 300000
    } = options;

    if (!text || text.trim().length === 0) {
      throw new Error('Text input cannot be empty');
    }

    if (speed < 0.5 || speed > 2.0) {
      throw new Error('Speed must be between 0.5 and 2.0');
    }

    const data = {
      input_text: text.trim(),
      speed,
      audio_format: audioFormat
    };

    if (language) {
      (data as any).language = language.toLowerCase();
    }

    const response = await this.client.post<any>(`/voice/voices/${voiceId}/generate`, data);

    if (response.job_id) {
      // It's an async job
      const job = { id: response.job_id, ...response };

      if (waitForCompletion) {
        const result = await this.client.waitForJobCompletion(job.id, timeout);
        return {
          job: { ...job, ...result },
          outputUrl: result.output_url || result.outputUrl,
          duration: result.duration
        };
      }

      return { job };
    } else {
      // Direct response with audio URL
      return {
        job: { id: 0, status: 'completed' } as Job,
        outputUrl: response.output_url || response.outputUrl,
        duration: response.duration
      };
    }
  }

  /**
   * List available voice profiles
   */
  async listVoiceProfiles(options: {
    voiceType?: 'custom' | 'standard';
    isPublic?: boolean;
    includePublic?: boolean;
    limit?: number;
    skip?: number;
  } = {}): Promise<VoiceProfile[]> {
    const {
      voiceType,
      isPublic,
      includePublic = true,
      limit = 50,
      skip = 0
    } = options;

    const params: Record<string, any> = {
      limit,
      skip,
      include_public: includePublic
    };

    if (voiceType) {
      params.voice_type = voiceType;
    }

    if (isPublic !== undefined) {
      params.is_public = isPublic;
    }

    return this.client.get<VoiceProfile[]>('/voice/voice-profiles', params);
  }

  /**
   * Get details of a specific voice profile
   */
  async getVoiceProfile(voiceId: number | string): Promise<VoiceProfile> {
    return this.client.get<VoiceProfile>(`/voice/voice-profiles/${voiceId}`);
  }

  /**
   * Delete a voice profile
   */
  async deleteVoiceProfile(voiceId: number | string): Promise<void> {
    await this.client.delete(`/voice/voices/${voiceId}`);
  }

  /**
   * Get voice cloning job status
   */
  async getJobStatus(jobId: number): Promise<Job> {
    return this.client.get<Job>(`/voice/clone/${jobId}/status`);
  }

  /**
   * List voice cloning jobs
   */
  async listCloningJobs(options: {
    skip?: number;
    limit?: number;
    status?: string;
  } = {}): Promise<Job[]> {
    const params: Record<string, any> = {};

    if (options.skip !== undefined) {
      params.skip = options.skip;
    }
    if (options.limit !== undefined) {
      params.limit = options.limit;
    }
    if (options.status) {
      params.status = options.status;
    }

    return this.client.get<Job[]>('/voice/clone/jobs', params);
  }

  /**
   * Stream voice generation (WebSocket connection)
   */
  async streamVoiceGeneration(
    voiceId: number | string,
    text: string,
    options: {
      language?: string;
      speed?: number;
      onProgress?: (progress: number) => void;
      onAudioChunk?: (chunk: ArrayBuffer) => void;
      onComplete?: (result: any) => void;
      onError?: (error: any) => void;
    } = {}
  ): Promise<void> {
    const { language, speed = 1.0, onProgress, onAudioChunk, onComplete, onError } = options;

    try {
      const WebSocket = (await import('ws')).default;
      const config = this.client.getConfig();

      const wsUrl = `${config.baseURL.replace('http', 'ws')}/api/v1/voice/ws/stream`;
      const ws = new WebSocket(wsUrl, {
        headers: {
          'Authorization': `Bearer ${config.apiKey}`
        }
      });

      ws.on('open', () => {
        const message = {
          type: 'voice_generation',
          data: {
            voice_id: voiceId,
            input_text: text,
            target_language: language,
            speed
          }
        };
        ws.send(JSON.stringify(message));
      });

      ws.on('message', (data: Buffer) => {
        try {
          const message = JSON.parse(data.toString());

          switch (message.type) {
          case 'progress':
            if (onProgress) {
              onProgress(message.data.progress);
            }
            break;
          case 'audio_chunk':
            if (onAudioChunk) {
              const audioData = Buffer.from(message.data.chunk, 'base64');
              onAudioChunk(audioData.buffer);
            }
            break;
          case 'complete':
            if (onComplete) {
              onComplete(message.data);
            }
            ws.close();
            break;
          case 'error':
            if (onError) {
              onError(new Error(message.data.message));
            }
            ws.close();
            break;
          }
        } catch (error) {
          if (onError) {
            onError(error);
          }
        }
      });

      ws.on('error', (error) => {
        if (onError) {
          onError(error);
        }
      });

    } catch (error) {
      if (onError) {
        onError(error);
      }
    }
  }

  /**
   * Convert source audio to match target voice characteristics
   */
  async convertVoice(request: VoiceConversionRequest): Promise<VoiceConversionResult> {
    const {
      sourceFile,
      url,
      voiceUuid,
      waitForCompletion = false,
      timeout = 600000
    } = request;

    // Validate inputs
    if (!sourceFile && !url) {
      throw new Error('Either sourceFile or url must be provided');
    }

    if (sourceFile && url) {
      throw new Error('Cannot provide both sourceFile and url');
    }

    if (!voiceUuid || voiceUuid.trim().length === 0) {
      throw new Error('Voice UUID cannot be empty');
    }

    let job: Job;

    if (url) {
      // URL-based conversion
      const formData: Record<string, any> = {
        url,
        voice_uuid: voiceUuid.trim()
      };

      job = await this.client.post<Job>('/voice/voice-convert', formData);
    } else {
      // File-based conversion
      let fileData: File | Blob;

      if (typeof sourceFile === 'string') {
        const fs = await import('fs');
        const path = await import('path');

        if (!fs.existsSync(sourceFile)) {
          throw new Error(`Source audio file not found: ${sourceFile}`);
        }

        const fileBuffer = fs.readFileSync(sourceFile);
        const fileName = path.basename(sourceFile);
        fileData = new Blob([fileBuffer], { type: 'audio/*' });
        (fileData as any).name = fileName;
      } else {
        fileData = sourceFile!;
      }

      const formData: Record<string, any> = {
        voice_uuid: voiceUuid.trim()
      };

      job = await this.client.uploadFile<Job>(
        '/voice/voice-convert',
        fileData,
        formData
      );
    }

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        outputUrl: result.output_path || result.outputUrl || result.output_url
      };
    }

    return {
      job,
      outputUrl: undefined
    };
  }

  /**
   * Generate multi-voice TTS with multiple speakers
   */
  async generateMultiVoiceTTS(request: MultiVoiceTTSRequest): Promise<MultiVoiceTTSResult> {
    const {
      segments,
      mixMode = 'sequential',
      outputFormat = 'wav',
      silenceDuration = 0.5,
      normalizeVolume = true,
      generationParams = {},
      waitForCompletion = false,
      timeout = 600000
    } = request;

    // Validate inputs
    if (!segments || segments.length === 0) {
      throw new Error('At least one segment must be provided');
    }

    if (segments.length > 50) {
      throw new Error('Maximum 50 segments allowed');
    }

    for (const segment of segments) {
      if (!segment.text || segment.text.trim().length === 0) {
        throw new Error('Segment text cannot be empty');
      }
      if (!segment.voiceId) {
        throw new Error('Voice ID must be specified for each segment');
      }
    }

    if (!['sequential', 'parallel', 'timed'].includes(mixMode)) {
      throw new Error('Mix mode must be sequential, parallel, or timed');
    }

    if (!['wav', 'mp3', 'ogg'].includes(outputFormat)) {
      throw new Error('Output format must be wav, mp3, or ogg');
    }

    const formData: Record<string, any> = {
      segments: JSON.stringify(segments),
      mix_mode: mixMode,
      output_format: outputFormat,
      silence_duration: silenceDuration,
      normalize_volume: normalizeVolume,
      generation_params: JSON.stringify(generationParams)
    };

    const job = await this.client.post<Job>('/voice/multi-voice-tts', formData);

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        outputUrl: result.output_path || result.outputUrl || result.output_url,
        totalDuration: result.total_duration || result.totalDuration,
        segmentsCount: result.segments_count || result.segmentsCount || segments.length,
        uniqueVoicesCount: result.unique_voices_count || result.uniqueVoicesCount
      };
    }

    return {
      job,
      outputUrl: undefined,
      totalDuration: undefined,
      segmentsCount: segments.length,
      uniqueVoicesCount: new Set(segments.map(s => s.voiceId)).size
    };
  }

  /**
   * Create a voice collection
   */
  async createVoiceCollection(request: VoiceCollectionRequest): Promise<VoiceCollectionResult> {
    const {
      name,
      description,
      isPublic = false,
      color,
      icon,
      voiceIds = []
    } = request;

    // Validate inputs
    if (!name || name.trim().length === 0) {
      throw new Error('Collection name cannot be empty');
    }

    if (name.length > 100) {
      throw new Error('Collection name cannot exceed 100 characters');
    }

    if (description && description.length > 500) {
      throw new Error('Description cannot exceed 500 characters');
    }

    if (color && !/^#[0-9A-Fa-f]{6}$/.test(color)) {
      throw new Error('Color must be a valid hex color code (e.g., #FF0000)');
    }

    if (icon && icon.length > 50) {
      throw new Error('Icon identifier cannot exceed 50 characters');
    }

    const data: Record<string, any> = {
      name: name.trim(),
      is_public: isPublic,
      voice_ids: voiceIds
    };

    if (description) {
      data.description = description.trim();
    }

    if (color) {
      data.color = color;
    }

    if (icon) {
      data.icon = icon;
    }

    const result = await this.client.post<VoiceCollectionResult>('/voice/collections', data);
    return result;
  }

  /**
   * List voice collections
   */
  async listVoiceCollections(options: {
    includeVoices?: boolean;
    includePublic?: boolean;
  } = {}): Promise<VoiceCollection[]> {
    const { includeVoices = false, includePublic = true } = options;

    const params: Record<string, any> = {
      include_voices: includeVoices,
      include_public: includePublic
    };

    return this.client.get<VoiceCollection[]>('/voice/collections', params);
  }

  /**
   * Update a voice collection
   */
  async updateVoiceCollection(
    collectionId: number,
    updates: Partial<VoiceCollectionRequest>
  ): Promise<VoiceCollectionResult> {
    const data: Record<string, any> = {};

    if (updates.name !== undefined) {
      if (!updates.name || updates.name.trim().length === 0) {
        throw new Error('Collection name cannot be empty');
      }
      data.name = updates.name.trim();
    }

    if (updates.description !== undefined) {
      data.description = updates.description?.trim();
    }

    if (updates.isPublic !== undefined) {
      data.is_public = updates.isPublic;
    }

    if (updates.color !== undefined) {
      if (updates.color && !/^#[0-9A-Fa-f]{6}$/.test(updates.color)) {
        throw new Error('Color must be a valid hex color code');
      }
      data.color = updates.color;
    }

    if (updates.icon !== undefined) {
      data.icon = updates.icon;
    }

    const result = await this.client.put<VoiceCollectionResult>(
      `/voice/collections/${collectionId}`,
      data
    );
    return result;
  }

  /**
   * Delete a voice collection
   */
  async deleteVoiceCollection(collectionId: number): Promise<void> {
    await this.client.delete(`/voice/collections/${collectionId}`);
  }

  /**
   * Add voices to a collection
   */
  async addVoicesToCollection(collectionId: number, voiceIds: number[]): Promise<void> {
    if (!voiceIds || voiceIds.length === 0) {
      throw new Error('At least one voice ID must be provided');
    }

    const data = {
      voice_ids: voiceIds
    };

    await this.client.post(`/voice/collections/${collectionId}/voices`, data);
  }

  /**
   * Remove voices from a collection
   */
  async removeVoicesFromCollection(collectionId: number, voiceIds: number[]): Promise<void> {
    if (!voiceIds || voiceIds.length === 0) {
      throw new Error('At least one voice ID must be provided');
    }

    // Use post method to remove voices from collection
    await this.client.post(`/voice/collections/${collectionId}/voices/remove`, {
      voice_ids: voiceIds
    });
  }

  /**
   * Generate public voice preview (no authentication required)
   */
  async generatePublicPreview(request: PublicPreviewRequest): Promise<PublicPreviewResult> {
    const {
      voiceIdentifier,
      inputText,
      targetLanguage = 'en'
    } = request;

    // Validate inputs
    if (!voiceIdentifier || voiceIdentifier.trim().length === 0) {
      throw new Error('Voice identifier cannot be empty');
    }

    if (!inputText || inputText.trim().length === 0) {
      throw new Error('Input text cannot be empty');
    }

    if (inputText.length > 2000) {
      throw new Error('Input text cannot exceed 2000 characters');
    }

    const formData = {
      voice_identifier: voiceIdentifier.trim(),
      input_text: inputText.trim(),
      target_language: targetLanguage
    };

    const result = await this.client.post<any>('/voice/public/preview-generate', formData);

    return {
      audioUrl: result.audio_url || result.audioUrl,
      duration: result.duration
    };
  }

  /**
   * Populate provider voices (admin only)
   */
  async populateProviderVoices(provider: 'openai' | 'google_gemini' | 'audiopod_sonic'): Promise<{ message: string; count: number }> {
    if (!['openai', 'google_gemini', 'audiopod_sonic'].includes(provider)) {
      throw new Error('Invalid provider. Must be openai, google_gemini, or audiopod_sonic');
    }

    const result = await this.client.post<any>(`/voice/voices/populate/${provider}`);

    return {
      message: result.message,
      count: result.voices_created || result.count || 0
    };
  }

  /**
   * Get unified TTS job status (works for both single and multi-voice TTS)
   */
  async getUnifiedJobStatus(jobId: number): Promise<Job> {
    return this.client.get<Job>(`/voice/tts-jobs/${jobId}/status`);
  }

  /**
   * Get unified TTS history (both single and multi-voice jobs)
   */
  async getUnifiedTTSHistory(options: {
    limit?: number;
    offset?: number;
    status?: string;
    jobType?: 'single' | 'multi';
  } = {}): Promise<Job[]> {
    const { limit = 50, offset = 0, status, jobType } = options;

    const params: Record<string, any> = {
      limit,
      offset
    };

    if (status) {
      params.status = status;
    }

    if (jobType) {
      params.job_type = jobType;
    }

    return this.client.get<Job[]>('/voice/tts-jobs/history', params);
  }
}
